import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import notify from 'devextreme/ui/notify';

import stringify from '../utils/stableStringify';
import hash from 'hash-sum';
import { unpack, pack } from 'msgpackr';
import AntLocalization from '../utils/AntLocalization';
import AntLocalStorage from '../utils/AntLocalStorage';
import Box, { Item as BoxItem } from 'devextreme-react/box';

import AntConfig from '../utils/AntConfig';
import AntCondition from '../utils/AntCondition';
import AntString from '../utils/AntString';
import AntArray from '../utils/AntArray';

import AntSimpleDataSource from '../components/AntSimpleDataSource';
import AntCardViewList from './AntPOSCardViewList';
import AntPOSCardViewDragableList from './AntPOSCardViewDragableList';
import AntCardViewSearchBar from './AntPOSCardViewSearchBar';
import '../components/AntWidgetSkeleton/index.scss';
import AntPOSCategories from './AntPOSCategories';

import { AntGlobal, AntSession } from '../utils/AntContext';
import AntJSON from '../utils/AntJSON';
import AntAuth from '../utils/AntAuth';
import { getCollectionSnapshotReconciliation } from '../utils/AntSnapshotTransport';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    const session = AntSession();

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const compName = props.storageKey
        ? props.storageKey
        : props.name
        ? props.name
        : props.API && props.API.entityName
        ? props.API.entityName
        : props.entityName
        ? props.entityName
        : props.url
        ? props.url
        : undefined;

    const [shouldRender, setShouldRender] = useState(1);
    const [shouldQuery, setShouldQuery] = useState(1);

    const isRender = useMemo(() => shouldRender + (props.shouldRender ? props.shouldRender : 1), [props.shouldRender, shouldRender]);
    const isQueryNow = useMemo(() => shouldQuery + (props.shouldQuery ? props.shouldQuery : 1), [props.shouldQuery, shouldQuery]);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const forceQuery = useCallback(() => {
        shouldQuery((v) => v + 1);
    }, []);

    const [isScanBarcode, setIsScanBarcode] = useState(false);

    const [searchTextByBarcode, setSearchTextByBarcode] = useState('');

    const loading = useRef(false);
    const loaded = useRef(false);

    const cardview = useRef();
    const searchBar = useRef();
    const isForeground = useRef(false);
    isForeground.current = props.isForeground !== false;
    const autoRefreshTimer = useRef();
    const afterActionRefreshTimeout = useRef();
    const snapshotWriteTimer = useRef();
    const snapshotNetworkVersion = useRef(0);
    const restoredSnapshotKeys = useRef(new Set());
    const snapshotStorageDisabled = useRef(false);
    const localCollectionSnapshotStates = useRef(new Map());
    const collectionSnapshotStates = props.collectionSnapshotStates instanceof Map
        ? props.collectionSnapshotStates
        : localCollectionSnapshotStates.current;
    const fixedConditionsEffectMounted = useRef(false);
    const categoriesRightEffectMounted = useRef(false);

    const [itemsCats1, setItemsCats1] = useState([]);
    const [itemsCats1Loaded, setItemsCats1Loaded] = useState(false);
    const [selectedCats1, setSelectedCats1] = useState([]);
    const [cachedCategoryValues, setCachedCategoryValues] = useState([]);

    const publishCachedCategoryValues = useCallback(() => {
        if (!props.cacheData || !(props.searchCategories1?.lookups instanceof Array)) return;

        const fields = props.searchCategories1.lookups
            .map((lookup) => (typeof lookup === 'object' ? lookup.field || lookup.name : lookup))
            .filter(Boolean);
        const rows = cardViewDataSource.current?.getCachedSnapshotRows?.() || [];
        if (fields.length === 0 || !(rows instanceof Array) || rows.length === 0) return;

        const keys = new Set();
        const values = [];
        rows.forEach((row) => {
            fields.forEach((field) => {
                const sourceFields = tableName === 'ARItemStatus' && field === 'catalogue1' ? ['catalogue1', 'catalogue2', 'catalogue3'] : [field];
                const rowValues = sourceFields.map((sourceField) => (row?.[sourceField] instanceof Array ? row[sourceField] : [row?.[sourceField]])).flat();
                rowValues
                    .filter((value) => value !== undefined && value !== null && value !== '')
                    .forEach((value) => {
                        const key = stringify(value);
                        if (keys.has(key)) return;
                        keys.add(key);
                        values.push(value);
                    });
            });
        });

        setCachedCategoryValues((previous) => (stringify(previous) === stringify(values) ? previous : values));
    }, [props.cacheData, props.searchCategories1, tableName]);

    const searchCategories1 = useMemo(() => {
        if (!props.searchCategories1 || cachedCategoryValues.length === 0) return props.searchCategories1;
        return { ...props.searchCategories1, values: cachedCategoryValues };
    }, [cachedCategoryValues, props.searchCategories1]);

    const selectedCategories = [selectedCats1]
        .flat()
        .map((s) => s.value)
        .flat();

    const getSearchCategories = useCallback(() => {
        let ar = [];
        if (
            props.searchCategories1 &&
            props.searchCategories1.lookups instanceof Array &&
            props.searchCategories1.lookups.length > 0 &&
            itemsCats1.length > 0 &&
            selectedCats1.filter((s) => s.value && s.value.length > 0)
        )
            ar.push({
                categories: props.searchCategories1.lookups,
                items: itemsCats1,
                selectedItems: selectedCats1
            });

        return ar;
    }, [props.searchCategories1, itemsCats1, selectedCats1]);

    const defaultPageSize = props.defaultPageSize ? props.defaultPageSize : 100;
    const cacheRefreshSeconds = props.cacheData ? Number(props.cacheRefreshSeconds || props.cacheSeconds || 60 * 60) : 0;

    const cardViewDataSource = useRef(
        new AntSimpleDataSource({
            remotePaging: props.cardview && props.cardview?.dragable ? false : props.remotePaging,
            pageSize: defaultPageSize,
            cacheData: props.cacheData,
            cacheName: props.cacheName,
            cacheSeconds: props.cacheSeconds,
            cacheByPassFields: props.cacheByPassFields,
            searchTextFields: props.searchTextFields
        })
    );

    const searchConditions = useMemo(() => {
        return { ...props.searchConditions };
    }, []);

    const [finalSearchConditions, setFinalSearchConditions] = useState({});
    const finalSearchConditionsRef = useRef(finalSearchConditions);
    finalSearchConditionsRef.current = finalSearchConditions;
    const externalInitialReloadCompleted = useRef(props.initialReloadHandledExternally !== true);
    const [categoriesRightSearchConditions, setCategoriesRightSearchConditions] = useState(
        () => props.initialCategoriesRightSearchConditions || {}
    );
    const snapshotCacheGroup = props.snapshotCache
        ? [
              `${props.snapshotCacheName || props.cacheName || tableName || 'SearchList'}-LiveSnapshot`,
              'schema:1',
              AntAuth.getSessionInfo('business_code') || '',
              AntAuth.getSessionInfo('user_code') || '',
              AntAuth.getSessionInfo('store_code') || '',
              hash(AntAuth.getSessionToken() || '')
          ].join('|')
        : null;

    const getSnapshotCacheKey = useCallback(
        (conditions) => {
            if (!snapshotCacheGroup || !conditions || Object.keys(conditions).length === 0) return null;
            const value = { ...conditions };
            delete value.forceQuery;
            delete value.forceRecalc;
            delete value.pageIndex;
            delete value.pageSize;
            return stringify(value);
        },
        [snapshotCacheGroup]
    );

    const writeSnapshot = useCallback(
        (rows, conditions) => {
            if (!snapshotCacheGroup || snapshotStorageDisabled.current || !(rows instanceof Array)) return;
            const key = getSnapshotCacheKey(conditions || finalSearchConditionsRef.current);
            if (!key) return;

            clearTimeout(snapshotWriteTimer.current);
            snapshotWriteTimer.current = setTimeout(() => {
                AntLocalStorage.setData(snapshotCacheGroup, key, rows, props.snapshotCacheSeconds || 60 * 30)
                    .then((stored) => {
                        if (stored === false) snapshotStorageDisabled.current = true;
                    })
                    .catch(() => {
                        snapshotStorageDisabled.current = true;
                    });
            }, props.snapshotCacheWriteDelay || 200);
        },
        [getSnapshotCacheKey, props.snapshotCacheSeconds, props.snapshotCacheWriteDelay, snapshotCacheGroup]
    );

    const [searchText, setSearchText] = useState('');

    const initGridRowActions = useCallback(() => {
        var actions = [];

        if (props) {
            if (props.showActions) {
                let acts = config.getCnfSearchActions(
                    props.rowActionPack ? props.rowActionPack : props.table ? props.table : props.API ? props.API.entityName : null
                );
                actions.push(...acts);
            }

            if (props.rowActions instanceof Array)
                props.rowActions.forEach((item) => {
                    actions.push(item);
                });

            if (props.excludesRowActions instanceof Array) {
                actions = actions.filter((item) => {
                    return !props.excludesRowActions.some((r) => r === item.code);
                });
            }

            actions = actions.filter((item) => {
                return AntCondition.isVisible(null, item);
            });
        }

        return actions;
    }, [props.showActions, props.rowActionPack, props.table, props.API, props.rowActions, props.excludesRowActions]);

    const gridRowActions = useMemo(() => initGridRowActions(), [props.showActions, props.rowActions, props.excludesRowActions, props.rowActionPack]);

    const dsGetSearchText = useCallback(() => {
        if (isScanBarcode) return searchTextByBarcode;
        return searchText;
    }, [isScanBarcode, searchTextByBarcode, searchText]);

    const dsGetPageSize = useCallback(() => {
        return defaultPageSize;
    }, [defaultPageSize]);

    const dsGetQueryFields = useCallback(
        (opts) => {
            var ar = [];
            ar = ['created_at', 'checksum', 'id', 'code'];

            if (props.showDetail) {
                if (
                    typeof props.showDetail === 'object' &&
                    props.showDetail.key &&
                    !ar.includes(props.showDetail.key) &&
                    !ar.some((a) => a && typeof a === 'object' && a.field === props.showDetail.key)
                ) {
                    ar.push(props.showDetail.key);
                } else if (props.showDetail instanceof Array) {
                    for (let i = 0; i < props.showDetail.length; i++) {
                        const item = props.showDetail[i];

                        if (
                            typeof item === 'object' &&
                            item.key &&
                            !ar.includes(item.key) &&
                            !ar.some((a) => a && typeof a === 'object' && a.field === item.key)
                        ) {
                            ar.push(item.key);
                        }
                    }
                }
            }

            if (props.queryFields instanceof Array) ar.push(...props.queryFields);
            if (props.selectFields instanceof Array) ar.push(...props.selectFields);

            if (props.getQueryFields) {
                var fields = props.getQueryFields();
                if (fields instanceof Array) ar.push(...fields);
            }

            if (props.showDetail) ar.push(...AntJSON.getKeys(props.showDetail));
            if (props.cardview) ar.push(...AntJSON.getKeys(props.cardview));
            if (props.rowStyles) ar.push(...AntJSON.getKeys(props.rowStyles));
            if (props.rowActions) ar.push(...AntJSON.getKeys(props.rowActions));

            if (gridRowActions) ar.push(...AntJSON.getKeys(gridRowActions));

            if (props.cardview) {
                for (let i = 0; i < config.cnfFields.length; i++) {
                    const cnf = config.cnfFields[i];

                    if (ar.includes(cnf.dataField)) {
                        if (cnf.visibleOnMain) ar.push(...AntJSON.getKeys(cnf.visibleOnMain));
                        if (cnf.visibleSelf) ar.push(...AntJSON.getKeys(cnf.visibleSelf));
                    }
                }
            }

            ar = ar.flat();
            ar = ar
                .filter((v) => Boolean(v))
                .filter((v, index, self) => {
                    return self.indexOf(v) === index;
                });

            // The API contract for selectFields is string[]. Keeping the original
            // field descriptor here can serialize to `{}` in a production bundle
            // (for example when `field` is exposed by a getter), which makes the
            // backend strip every requested product field in minimizeData mode.
            const fieldNames = ar
                .map((field) => (field && typeof field === 'object' ? field.field : field))
                .filter((field) => typeof field === 'string' && field.length > 0)
                .filter((field) => config.hasField(field));

            return AntArray.distinct(fieldNames);
        },
        [
            config,
            gridRowActions,
            props.table,
            props.API,
            props.columns,
            props.showDetail,
            props.queryFields,
            props.selectFields,
            props.getQueryFields,
            props.cardview,
            props.rowStyles,
            props.rowActions
        ]
    );
    const dsGetQueryFieldsRef = useRef(dsGetQueryFields);
    dsGetQueryFieldsRef.current = dsGetQueryFields;

    const dsGetSearchConditions = useCallback(() => {
        return searchConditions.current ? JSON.parse(stringify(searchConditions.current)) : {};
    }, []);

    const dsGetOrderBy = useCallback(() => {
        if (props.orderBy) return props.orderBy;
        return null;
    }, [props.orderBy]);

    const dsGetOrderType = useCallback(() => {
        if (props.orderType) return props.orderType;
        return null;
    }, [props.orderType]);

    const dsGetOrderings = useCallback(() => {
        if (props.ordering instanceof Array) return props.ordering;
        return null;
    }, [props.ordering]);

    const isAllowCardViewQuery = useCallback(() => {
        if (props.searchConditionsRequired && (!searchConditions.current || Object.keys(searchConditions.current).length === 0)) return false;

        if (props.isAllowQuery && typeof props.isAllowQuery === 'function') return props.isAllowQuery();

        return true;
    }, [props.searchConditionsRequired, props.isAllowQuery]);

    const isDataLoading = useCallback(() => {
        return Boolean(loading);
    }, [loading]);

    const isDataLoaded = useCallback(() => {
        return Boolean(loaded.current);
    }, []);

    const renderDetailLink = useCallback(
        (rowData) => {
            var ar = [];
            if (props.showDetail && props.showDetail instanceof Array) {
                for (let i = 0; i < props.showDetail.length; i++) {
                    const item = props.showDetail[i];
                    ar.push(item);
                }
            } else if (props.showDetail && typeof props.showDetail === 'object')
                ar.push({
                    when: props.showDetail.when,
                    whenType: props.showDetail.whenType,
                    url: props.showDetail.url,
                    key: props.showDetail.key
                });

            for (let i = 0; i < ar.length; i++) {
                const item = ar[i];

                if (item.when) {
                    var isValid = true;

                    var isValid = AntCondition.isValid(rowData, {
                        when: item.when,
                        whenType: item.whenType
                    });

                    if (!isValid) continue;
                }

                var url = item.url ? item.url : null;
                var key = null;

                if (url) {
                    if (item.key) {
                        key = item.key;
                        if (!rowData[key]) continue;
                    }

                    if (!key || !rowData[key]) key = 'code';
                    if (!key || !rowData[key]) key = 'id';
                    if (!key || !rowData[key]) key = props.keyExpr;

                    if (key && rowData[key]) {
                        var value = rowData[key];

                        var linkUrl = format('{0}/{1}', url, value);
                        return {
                            url: linkUrl,
                            value: value,
                            rowData: rowData
                        };
                    }
                }
            }

            return null;
        },
        [props.keyExpr, props.showDetail]
    );

    // const onRowDblClick = useCallback(
    //     (e) => {
    //         if (e && e.data) {
    //             var lnk = renderDetailLink(e.data);
    //             if (lnk && lnk.url) {
    //                 navigate(lnk.url);
    //                 return;
    //             }
    //         }
    //         // if (e.data && e.data.id) showEditPopup(e.data.id);

    //         if (props.onRowDblClick) props.onRowDblClick(e);
    //     },
    //     [renderDetailLink, props.onRowDblClick]
    // );

    const generateSearchConditions = useCallback(
        (opts) => {
            let objS = {};

            if (props.searchCategories1)
                objS = selectedCats1.reduce((obj, c) => {
                    if (obj[c.field] !== undefined) obj[c.field].push(c.value);
                    else obj[c.field] = [c.value];
                    return obj;
                }, objS);

            Object.keys(objS).forEach((k) => {
                if (objS[k] instanceof Array && objS[k].length === 1) objS[k] = objS[k][0];
            });

            let newObj = {};

            if (props.searchConditions) {
                if (typeof props.searchConditions === 'function') {
                    var obj = props.searchConditions();
                    if (obj)
                        newObj = {
                            ...newObj,
                            ...obj
                        };
                } else {
                    newObj = {
                        ...newObj,
                        ...props.searchConditions
                    };
                }
            }

            newObj = { ...newObj, ...categoriesRightSearchConditions };

            if (props.searchFields instanceof Array && props.searchFields.length > 0) {
                Object.keys(newObj).map((k) => {
                    var found = props.searchFields.find((f) => typeof f === 'object' && f.alias && f.field === k);
                    if (found) {
                        newObj[found.alias] = newObj[k];
                        delete newObj[k];
                    }
                });

                Object.keys(newObj).map((k) => {
                    var found = props.searchFields.find(
                        (f) =>
                            f === k ||
                            f === k + '_oneMonth' ||
                            f === k + '_oneYear' ||
                            f === k + '_startDate' ||
                            f === k + '_startMonth' ||
                            f === k + '_startYear' ||
                            f === k + '_endDate' ||
                            f === k + '_endMonth' ||
                            f === k + '_endYear' ||
                            k === f + '_oneMonth' ||
                            k === f + '_oneYear' ||
                            k === f + '_startDate' ||
                            k === f + '_startMonth' ||
                            k === f + '_startYear' ||
                            k === f + '_endDate' ||
                            k === f + '_endMonth' ||
                            k === f + '_endYear' ||
                            (typeof f === 'object' && f.alias === k)
                    );

                    if (!found) newObj[k] = undefined;
                });
            }

            newObj = { ...newObj, ...objS };

            Object.keys(newObj).map((k) => {
                if (newObj[k] !== null && typeof newObj[k] === 'object') newObj[k] = AntCondition.getValueFromSession(newObj[k]);
            });

            Object.keys(newObj).map((k) => {
                if (newObj[k] === null || newObj[k] === '') newObj[k] = undefined;
            });

            Object.keys(newObj).forEach((key) => {
                if (newObj[key] === undefined) {
                    delete newObj[key];
                }
            });

            Object.keys(newObj).forEach((key) => {
                if (newObj[key] instanceof Array) {
                    newObj[key + '_search_inner_join'] = true;
                }
            });

            if (props.isSearching) newObj.isSearching = true;

            if (!opts || !opts.byPassCheckActions) {
                if (gridRowActions instanceof Array && gridRowActions.length > 0) {
                    // var selectedGroup = filteredGroup.current;

                    newObj.checkActions = gridRowActions
                        // .filter(
                        //     (r) => !selectedGroup || !r.filterGroups || r.filterGroups instanceof Array === false || r.filterGroups.includes(r.code)
                        // )
                        .map((a) => a.code);
                }
            }

            searchConditions.current = newObj;
            if (stringify(newObj) !== stringify(finalSearchConditionsRef.current)) setFinalSearchConditions(newObj);

            return newObj;
        },
        [
            props.cardview,
            props.searchConditions,
            categoriesRightSearchConditions,
            props.searchFields,
            props.isSearching,
            props.showActions,
            props.rowActionPack,
            props.rowActionWidth,
            props.rowActions,
            gridRowActions,
            selectedCats1
        ]
    );

    const refresh = useCallback(
        (changesOnly) => {
            generateSearchConditions();

            // forceRender();
        },
        [generateSearchConditions, forceRender]
    );

    const reload = useCallback(
        async (opts) => {
            refresh(true);
        },
        [refresh]
    );

    const forceReload = useCallback((options = {}) => {
        if (
            options.allowBackground !== true &&
            (!isForeground.current || (typeof document !== 'undefined' && document.visibilityState === 'hidden'))
        )
            return false;
        generateSearchConditions({ autoReload: true });
        // autoReload();
        // if (dataSource.current) dataSource.current.reset(null, false, false);

        if (props.cardview) {
            if (cardview.current) {
                if (options.forceQuery === true && props.cacheData) cardViewDataSource.current.requestCacheRefresh();
                cardview.current.reload();
            }
        }
    }, [generateSearchConditions, props.cacheData, props.cardview]);

    const onRowAction = useCallback(
        async (e, done) => {
            let actionName = e.actionId;
            if (actionName === 'actionCancel') actionName = 'cancel';
            if (actionName === 'actionDelete') actionName = 'delete';
            if (actionName === 'actionRelease') actionName = 'release';
            if (actionName === 'actionClose') actionName = 'close';
            if (actionName === 'actionApprove') actionName = 'approve';
            if (actionName === 'actionPost') actionName = 'post';
            if (actionName === 'actionReject') actionName = 'reject';

            if (props.onRowAction) {
                let close = await props.onRowAction({
                    actionName: actionName,
                    table: tableName,
                    mainCode: e.data?.code,
                    mainID: e.data?.id,
                    mainData: e.data,
                    stepValue: e.stepValue
                });
                if (close) {
                    if (done) done();
                    return close;
                }
            }

            var btn = gridRowActions instanceof Array ? gridRowActions.find((a) => a.code === e.actionId) : null;
            if (!btn) {
                if (done) done();
                return;
            }

            var actAPI = btn.API ? btn.API : props.API;

            var result;

            if (props.rowActionCustomData) {
                let data = props.rowActionCustomData(e);
                result = await actAPI.postActionWithData(actionName, data);
            } else {
                result = await actAPI.postAction(actionName, e.data.code, e.data.id);
            }

            // Lời xoá bị luật configDelete chặn: hiện lý do của luật (trước đây lưới chỉ nạp lại, không báo gì).
            if (result && typeof result.error === 'string' && result.error && actionName === 'delete') notify(result.error, 'error', 5000);

            // if (result && result.data) {
            //     var code = result.data.code;

            //     if (btn && btn.navigateLink && code) {
            //         var value;
            //         var link;

            //         if (btn.navigateLink instanceof Array) {
            //             btn.navigateLink.forEach((l) => {
            //                 if (l && l.link) {
            //                     if (l.when && typeof l.when === 'object') {
            //                         var isValid = AntCondition.isValid(result.data, {
            //                             when: l.when,
            //                             whenType: l.whenType
            //                         });
            //                         if (!isValid) return;
            //                     }
            //                     value = l.field ? result.data[l.field] : result.data.code;
            //                     link = String(l.link);
            //                 }
            //             });
            //         } else {
            //             value = btn.navigateField ? result.data[btn.navigateField] : result.data.code;
            //             link = String(btn.navigateLink);
            //         }

            //         if (link && value !== undefined && value !== null) {
            //             if (!link.startsWith('/')) link = '/' + link;
            //             if (!link.endsWith('/')) link = link + '/';
            //             link = link + value;
            //             navigate(link);
            //         }
            //     }
            // }

            if (btn && (!btn.opts || !btn.opts.byPassRefreshGrid) && props.refreshAfterAction !== false) {
                await forceReload({ forceQuery: true });

                if (afterActionRefreshTimeout.current) clearTimeout(afterActionRefreshTimeout.current);
                afterActionRefreshTimeout.current = setTimeout(() => {
                    forceReload({ forceQuery: true });
                    // if (grid.current && grid.current.instance) grid.current.instance.repaint();
                }, 1500);
            }
            if (props.afterRowAction)
                props.afterRowAction({
                    actionName: actionName,
                    table: tableName,
                    mainCode: e.data.code,
                    mainID: e.data.id,
                    mainData: e.data,
                    actionResult: result
                });

            if (done) done();

            return result;
        },
        [reload, props.refreshAfterAction, props.onRowAction, gridRowActions, props.API, props.rowActionCustomData, props.afterRowAction]
    );

    const initCardViewDataSourceConfig = useCallback(() => {
        return {
            key: props.keyExpr,
            API: props.API,
            url: props.url,
            loadFunc: props.loadFunc,
            remotePaging: props.cardview && props.cardview?.dragable ? false : props.remotePaging !== undefined ? props.remotePaging : true,
            isScrolling: true,

            getPageSize: dsGetPageSize,
            getSearchConditions: dsGetSearchConditions,
            // The datasource survives renders in a ref. Resolve the current
            // callback lazily so production never keeps the first render's
            // field metadata, without making this re-init effect reload on
            // every newly allocated cardview object.
            getQueryFields: (...args) => dsGetQueryFieldsRef.current(...args),
            getSearchText: dsGetSearchText,
            getOrderBy: dsGetOrderBy,
            getOrderType: dsGetOrderType,
            getOrderings: dsGetOrderings,
            // isAllowQuery: isAllowCardViewQuery,

            onGotData: (data, allData) => {
                if (cardview.current) cardview.current.onLoadData(data, allData);
                publishCachedCategoryValues();
                snapshotNetworkVersion.current += 1;
                const rows = data && data.data instanceof Array ? data.data : allData instanceof Array ? allData : [];
                writeSnapshot(rows);
            },
            cacheData: props.cacheData,
            cacheName: props.cacheName,
            cacheSeconds: props.cacheSeconds,
            cacheByPassFields: props.cacheByPassFields,
            searchTextFields: props.searchTextFields
        };
    }, [
        props.keyExpr,
        props.API,
        props.url,
        props.loadFunc,
        props.remotePaging,
        dsGetPageSize,
        dsGetSearchConditions,
        // dsGetQueryFields is consumed through dsGetQueryFieldsRef above.
        dsGetSearchText,
        dsGetOrderBy,
        dsGetOrderType,
        dsGetOrderings,
        writeSnapshot,
        publishCachedCategoryValues,
        props.cacheData,
        props.cacheName,
        props.cacheSeconds,
        props.cacheByPassFields,
        props.searchTextFields
        // isAllowCardViewQuery
    ]);

    const onItemMouseEnter = useCallback(
        (data) => {
            // if (props.autoShowActions !== false) onRowDblClick({ data: data });
        },
        [props.autoShowActions]
    );

    const onItemClick = useCallback(
        async (data) => {
            if (props.onCellClick) {
                let close = await props.onCellClick({
                    table: tableName,
                    mainCode: data?.code,
                    mainID: data?.id,
                    mainData: data
                });
                if (close) return;
            }

            // onRowDblClick({ data: data });
        },
        [props.onCellClick, tableName, onItemMouseEnter]
    );

    const focusSearchBox = useCallback(() => {
        if (searchBar.current) searchBar.current.focusSearchBox();
    }, []);

    const patchRows = useCallback(
        (rows, options = {}) => {
            const nextRows = cardViewDataSource.current.patchRows(rows, {
                orderBy: options.orderBy || props.orderBy,
                orderType: options.orderType || props.orderType,
                ...options
            });
            if (cardview.current) cardview.current.onLoadData(nextRows, nextRows);
            if (options.reloadFromCurrentData === true) {
                void cardViewDataSource.current
                    .reloadFromCurrentData()
                    .catch(() => cardview.current?.repaintPatchedRows?.());
            } else if (options.repaint === true) cardview.current?.repaintPatchedRows?.();
            if (!options.skipSnapshotCache) writeSnapshot(nextRows);
            return nextRows;
        },
        [props.orderBy, props.orderType, writeSnapshot]
    );

    const refreshRows = useCallback(
        async (searchPatch, options = {}) => {
            if (
                options.allowBackground !== true &&
                (!isForeground.current || (typeof document !== 'undefined' && document.visibilityState === 'hidden'))
            )
                return [];
            if (!props.API || !searchPatch) return [];
            if (options.completeInitialReload === true) externalInitialReloadCompleted.current = true;
            const requestedSelectFields = searchPatch.selectFields instanceof Array ? searchPatch.selectFields : [];
            const body = {
                ...finalSearchConditions,
                ...searchPatch,
                // A targeted caller can require display fields which are not
                // discoverable from a custom card renderer. Never overwrite
                // them with the generic field discovery list.
                selectFields: Array.from(new Set([...dsGetQueryFields(), ...requestedSelectFields])),
                recalculate: Boolean(options.recalculate),
                forceQuery: true,
                pageIndex: 0,
                pageSize: options.pageSize || 100
            };
            // `/list` requires AntAPI.getList: it converts minimized `f_N`
            // response fields into the requested field names before the card
            // datasource merges rows by serve_place_code.
            if (options.collectionSnapshot === true) {
                const stateKey = options.snapshotKey || options.actionName || 'actionGetSnapshots';
                const currentState = collectionSnapshotStates.get(stateKey) || {};
                const collection = await props.API.postCollectionSnapshot(
                    options.actionName || 'actionGetSnapshots',
                    body,
                    currentState,
                    {
                        identityField: options.identityField || 'id',
                        forceFull: options.forceFull === true,
                        snapshotTransportVersion: options.snapshotTransportVersion || 1
                    }
                );

                const shouldApplyResponse = typeof options.shouldApply !== 'function' || options.shouldApply();
                if (!shouldApplyResponse) {
                    // A socket may make this response obsolete for painting,
                    // but the server snapshot is still a valid network
                    // baseline. Preserve it when no newer collection response
                    // has advanced the same state in the meantime.
                    const latestState = collectionSnapshotStates.get(stateKey);
                    const baselineStillCurrent =
                        !latestState || latestState.snapshotRevision === currentState.snapshotRevision;
                    if (collection.state && options.forceFull !== true && baselineStillCurrent) {
                        collectionSnapshotStates.set(stateKey, collection.state);
                    }
                    return collection;
                }

                if (collection.state) {
                    const currentRows = cardViewDataSource.current?.getCurrentData?.() || [];
                    const renderedRowCount = cardview.current?.getRenderedRowCount?.();
                    const reconciliation = getCollectionSnapshotReconciliation(currentRows, collection.rows, {
                        ...options,
                        renderedRowCount
                    });
                    // Do not accept an empty baseline while cards are visible.
                    // Keeping the previous state forces the next timer tick to
                    // ask BE again instead of locking into not-modified forever.
                    if (reconciliation.shouldPreserveCurrent) return collection;
                    collectionSnapshotStates.set(stateKey, collection.state);
                    const shouldReconcileFullRows =
                        collection.isFull || !reconciliation.isCurrentComplete || reconciliation.shouldRepairBlankRender;
                    if (shouldReconcileFullRows) {
                        patchRows(collection.rows, {
                            ...options,
                            // Reconcile atomically through one Store.push batch.
                            // Clearing first lets DevExtreme render an empty page
                            // and a later not-modified response cannot repair it.
                            replaceAll: false,
                            removeMissing: true,
                            requestedValues: reconciliation.currentKeys,
                            // Full snapshots and membership changes must rebuild
                            // DevExtreme's local item collection. This reload is
                            // RAM-only; it never issues a second API request.
                            reloadFromCurrentData: true
                        });
                    } else if (collection.isDelta && (collection.changedRows.length > 0 || collection.removedKeys.length > 0)) {
                        patchRows(collection.changedRows, {
                            ...options,
                            requestedValues: [...collection.changedRows, ...collection.removedKeys.map((key) => ({
                                [options.identityField || 'id']: key
                            }))]
                                .map((row) => row?.[options.identityField || 'id'])
                                .filter((value) => value !== undefined && value !== null),
                            removeMissing: true
                        });
                    }
                    return collection;
                }

                // Compatibility path for a BE image that has not yet gained
                // collection envelopes. Keep the existing full-row behavior.
                if (collection.isLegacy && collection.rows instanceof Array) {
                    patchRows(collection.rows, {
                        ...options,
                        replaceAll: options.replaceAll !== false,
                        reloadFromCurrentData: true
                    });
                }
                return collection;
            }

            const result =
                options.actionName === 'list'
                    ? await props.API.getList(body, undefined, { latestKey: options.latestKey || 'refreshTabRows' })
                    : await props.API.postActionWithData(options.actionName || 'actionGetSnapshots', body);
            if (!result || result.error || result?.data?.error) return [];

            // A screen can issue overlapping lightweight socket refreshes. Let
            // the caller reject an obsolete response before it mutates the
            // datasource, so an old server snapshot cannot overwrite new state.
            if (typeof options.shouldApply === 'function' && !options.shouldApply()) return [];

            const payload = result?.data?.data || result?.data || result;
            const rows = result?.rows instanceof Array ? result.rows : payload?.rows instanceof Array ? payload.rows : payload instanceof Array ? payload : [];
            let requestedValues = options.requestedValues || searchPatch.serve_place_codes || searchPatch.serve_place_code || [];
            // A full-map patch needs to remove cards that no longer match the
            // current switcher/category filter. Derive the existing keys only
            // when the caller explicitly requests that behavior; targeted socket
            // patches keep their supplied key list unchanged.
            if (options.removeMissing && (!(requestedValues instanceof Array) || requestedValues.length === 0)) {
                const matchFields = options.matchFields instanceof Array ? options.matchFields : [];
                const currentRows = cardViewDataSource.current?.getCurrentData?.() || [];
                requestedValues = currentRows
                    .map((row) => matchFields.map((field) => row?.[field]).find((value) => value !== undefined && value !== null))
                    .filter((value) => value !== undefined && value !== null);
            }

            if (!options.skipSnapshotCache && snapshotCacheGroup) await AntLocalStorage.clearByKey(snapshotCacheGroup);
            if (typeof options.shouldApply === 'function' && !options.shouldApply()) return [];
            patchRows(rows, {
                ...options,
                requestedValues: requestedValues instanceof Array ? requestedValues : [requestedValues]
            });
            return rows;
        },
        [collectionSnapshotStates, dsGetQueryFields, finalSearchConditions, patchRows, props.API, snapshotCacheGroup]
    );

    useImperativeHandle(ref, () => ({
        forceReload: forceReload,
        refresh: refresh,
        forceRender: forceRender,
        isDataLoaded: isDataLoaded,
        isDataLoading: isDataLoading,
        isReady: () =>
            Boolean(cardview.current) &&
            Boolean(finalSearchConditionsRef.current) &&
            Object.keys(finalSearchConditionsRef.current).length > 0,
        focusSearchBox: focusSearchBox,
        getSearchConditions: () => finalSearchConditionsRef.current,
        patchRows: patchRows,
        refreshRows: refreshRows
    }));

    useEffect(() => {
        if (!snapshotCacheGroup) return undefined;
        const key = getSnapshotCacheKey(finalSearchConditions);
        if (!key || restoredSnapshotKeys.current.has(key)) return undefined;
        restoredSnapshotKeys.current.add(key);
        const networkVersion = snapshotNetworkVersion.current;
        let mounted = true;

        AntLocalStorage.getData(snapshotCacheGroup, key).then((rows) => {
            if (!mounted || snapshotNetworkVersion.current !== networkVersion || !(rows instanceof Array)) return;
            patchRows(rows, { replaceAll: true, appendMissing: true });
        });

        return () => {
            mounted = false;
        };
    }, [finalSearchConditions, getSnapshotCacheKey, patchRows, snapshotCacheGroup]);

    useEffect(() => () => clearTimeout(snapshotWriteTimer.current), []);

    useEffect(() => {
        cardViewDataSource.current.reInit(initCardViewDataSourceConfig());
        // Restore the cached snapshot first on mount. Timers, pull-to-refresh
        // and completed actions explicitly request the later live rebuild.
        if (props.initialReloadHandledExternally === true && !externalInitialReloadCompleted.current) {
            generateSearchConditions();
            return;
        }
        forceReload();
    }, [forceReload, generateSearchConditions, initCardViewDataSourceConfig, props.initialReloadHandledExternally]);

    useEffect(() => {
        if (!fixedConditionsEffectMounted.current) {
            fixedConditionsEffectMounted.current = true;
            return;
        }
        // A parent tab can change a fixed filter (for example, an occupied/all
        // switcher) without changing the selected category. Reload immediately.
        forceReload();
    }, [props.searchConditions, forceReload]);

    useEffect(() => {
        if (!categoriesRightEffectMounted.current) {
            categoriesRightEffectMounted.current = true;
            return;
        }
        forceReload();
    }, [categoriesRightSearchConditions, forceReload]);

    const onSearchText = useCallback(
        (text) => {
            setSearchText(text);
            if (props.onSearchText) props.onSearchText(text);
        },
        [props.onSearchText]
    );

    const onRefreshClick = useCallback(() => {
        // A user-triggered refresh is authoritative. The next collection request
        // must rebuild its baseline instead of accepting not-modified/delta.
        collectionSnapshotStates.clear();
        if (props.refreshHandledExternally !== true) forceReload({ forceQuery: true });
        if (props.onRefreshClick) props.onRefreshClick();
    }, [collectionSnapshotStates, forceReload, props.onRefreshClick, props.refreshHandledExternally]);

    const onScanBarcode = useCallback(
        (e, done) => {
            if (props.onScanBarcode)
                props.onScanBarcode(e, (r) => {
                    if (done) done(r);
                    if (!r) setSearchTextByBarcode(e);
                });
        },
        [props.onScanBarcode, setSearchTextByBarcode]
    );

    useEffect(() => {
        if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);

        const refreshSeconds = props.autoRefreshSeconds !== undefined && props.autoRefreshSeconds !== null ? props.autoRefreshSeconds : cacheRefreshSeconds;
        if (refreshSeconds)
            autoRefreshTimer.current = setInterval(() => {
                if (!isForeground.current) return;

                // A continuously visible POS grid such as the table map should
                // not be torn down and reloaded just to reconcile a timer tick.
                // Patch the returned rows instead: DevExtreme keeps its current
                // viewport/scroll position and React only receives the updated
                // card data. Explicit refresh and actions still use forceReload.
                if (props.autoRefreshMode === 'patch') {
                    void refreshRows(
                        props.autoRefreshSearchPatch || {},
                        {
                            pageSize: props.autoRefreshPageSize || 500,
                            matchFields: props.autoRefreshMatchFields,
                            appendMissing: true,
                            removeMissing: true,
                            orderBy: props.orderBy,
                            orderType: props.orderType
                        }
                    );
                    return;
                }

                forceReload({ forceQuery: true });
            }, refreshSeconds * 1000);
        return () => {
            if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
        };
    }, [
        cacheRefreshSeconds,
        forceReload,
        props.autoRefreshSeconds,
        props.autoRefreshMode,
        props.autoRefreshSearchPatch,
        props.autoRefreshPageSize,
        props.autoRefreshMatchFields,
        props.orderBy,
        props.orderType,
        refreshRows
    ]);

    // Listen for ARItemStatus recalculation events from the backend and automatically
    // clear the local cache and reload so users always see up-to-date item availability.
    useEffect(() => {
        if (!session || !session.registerSocketListener) return;

        const listenerName = (compName || 'AntPOSSearchList') + '-ARItemStatus';

        const onARItemStatusRecalculated = (data) => {
            if (data && data.event === 'ARItemStatus-Recalculated') {
                cardViewDataSource.current.clearCacheData();
                forceReload();
                return true;
            }
            return false;
        };

        session.clearSocketListener(listenerName);
        session.registerSocketListener(onARItemStatusRecalculated, listenerName);

        return () => {
            if (session && session.clearSocketListener) session.clearSocketListener(listenerName);
        };
    }, [compName, forceReload]);

    const isCategoriesShouldVisible = useCallback(
        (cats) => {
            if (!cats) return false;

            let isVisible = AntCondition.isVisible({}, cats);
            if (!isVisible) return false;
            if (cats.visible === true) return true;

            if (searchConditions.current && cats.lookups) {
                if (cats.lookups.some((l) => Object.keys(searchConditions.current).includes(l.field))) return true;
            }

            let ar1 = [props.searchCategories1];
            let ar2 = [selectedCats1.filter((s) => s.value && s.value.length > 0)];
            let ar3 = [itemsCats1];

            let ar4 = [itemsCats1Loaded];

            let currentCatIndex = ar1.findIndex((a) => stringify(a) === stringify(cats));
            let currentCatTotals = ar3[currentCatIndex];
            let currentCatSelecteds = ar2[currentCatIndex];
            let currentCatLoaded = ar4[currentCatIndex];

            if (!currentCatLoaded) return true;

            let _selectedCategories = [selectedCats1]
                .flat()
                .map((s) => s.value)
                .flat();

            if (cats.visibleDependOthers) {
                let _totalCats = itemsCats1.length;

                if (_totalCats > currentCatTotals.length) {
                    if (cats.visibleDependOthers instanceof Array) {
                        if (cats.visibleDependOthersType !== 'OR')
                            isVisible = cats.visibleDependOthers.every((oName) => {
                                for (let i = 0; i < ar1.length; i++) {
                                    const otherCat = ar1[i];
                                    const otherCatSelecteds = ar2[i];
                                    const otherCatTotals = ar3[i];
                                    if (otherCat && stringify(otherCat) !== stringify(cats)) {
                                        if (
                                            otherCat &&
                                            otherCat.lookups &&
                                            otherCat.lookups.some((l) => l.name === oName) &&
                                            (otherCatTotals.length <= 0 || otherCatSelecteds.length > 0)
                                        )
                                            return true;
                                    }
                                }

                                return false;
                            });
                        else {
                            isVisible = cats.visibleDependOthers.some((oName) => {
                                for (let i = 0; i < ar1.length; i++) {
                                    const otherCat = ar1[i];
                                    const otherCatSelecteds = ar2[i];
                                    const otherCatTotals = ar3[i];
                                    if (otherCat && stringify(otherCat) !== stringify(cats)) {
                                        if (
                                            otherCat &&
                                            otherCat.lookups &&
                                            otherCat.lookups.some((l) => l.name === oName) &&
                                            otherCatSelecteds.length > 0
                                        )
                                            return true;
                                    }
                                }

                                return false;
                            });

                            if (!isVisible)
                                isVisible = cats.visibleDependOthers.every((oName) => {
                                    for (let i = 0; i < ar1.length; i++) {
                                        const otherCat = ar1[i];
                                        const otherCatSelecteds = ar2[i];
                                        const otherCatTotals = ar3[i];
                                        if (otherCat && stringify(otherCat) !== stringify(cats)) {
                                            if (
                                                otherCat &&
                                                otherCat.lookups &&
                                                otherCat.lookups.some((l) => l.name === oName) &&
                                                otherCatTotals.length > 0
                                            )
                                                return false;
                                        }
                                    }

                                    return true;
                                });
                        }
                    } else {
                        isVisible = _selectedCategories.length > 0;
                    }
                }
            } else {
                isVisible = currentCatTotals.length > 1 || currentCatSelecteds.length > 0;
            }

            return isVisible;
        },
        [props.searchCategories1, itemsCats1, selectedCats1]
    );

    const cats1Visible = isCategoriesShouldVisible(props.searchCategories1);

    const onLoadedDataCat1 = useCallback((e) => {
        setItemsCats1(e.allItems ? e.allItems : []);
        setItemsCats1Loaded(true);
    }, []);

    const onSelectionChangedCat1 = useCallback(
        (e) => {
            setItemsCats1(e.allItems);
            setSelectedCats1(e.selectedItems);
            forceReload();
        },
        [forceReload]
    );

    const allowQueryCat1 = useCallback(
        (e) => {
            if (props.searchCategories1.visibleDependOthers) return selectedCategories.length > 0;
            return true;
        },
        [selectedCategories]
    );

    const setCategoriesRightConditions = useCallback((conditions) => {
        const next = conditions && typeof conditions === 'object' ? conditions : {};
        setCategoriesRightSearchConditions((current) => (stringify(current) === stringify(next) ? current : next));
    }, []);

    const categoriesRight =
        typeof props.renderCategoriesRight === 'function'
            ? props.renderCategoriesRight({ setSearchConditions: setCategoriesRightConditions })
            : props.renderCategoriesRight;
    const categoriesSearchConditions = useMemo(
        () => ({ ...searchConditions, ...categoriesRightSearchConditions }),
        [searchConditions, categoriesRightSearchConditions]
    );

    return (
        <React.Fragment>
            <Box direction="col" width="100%" height="100%" style={props.style}>
                <BoxItem baseSize={'auto'}>
                    {isRender && props.cardview && props.showSearchBar !== false && (
                        <AntCardViewSearchBar
                            ref={searchBar}
                            API={props.API}
                            cacheData={props.cacheData}
                            showSearchBox={props.showSearchBox}
                            showScanBarcode={props.showScanBarcode}
                            onSearchText={onSearchText}
                            onRefreshClick={onRefreshClick}
                            onScanBarcodeModeChanged={setIsScanBarcode}
                            onScanBarcode={onScanBarcode}
                        />
                    )}

                    {isRender && props.cardview && !isScanBarcode && (props.searchCategories1 || categoriesRight) && (
                        <div
                            className={props.categoriesHeaderClassName}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                                minHeight: 36,
                                marginTop: props.showSearchBar !== false ? '2px' : '10px'
                            }}
                        >
                            <div style={{ display: 'flex', flex: 1, minWidth: 0, overflowX: 'auto' }}>
                                {/* Keep the category row height stable while its data is loading. */}
                                {props.searchCategories1 && cats1Visible && !itemsCats1Loaded && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }} aria-hidden="true">
                                        {[64, 88, 72].map((w, i) => (
                                            <span key={i} className="ant-sk" style={{ width: w, height: 24, borderRadius: 12 }} />
                                        ))}
                                    </div>
                                )}

                                {props.searchCategories1 && (
                                    <div style={{ display: cats1Visible && itemsCats1Loaded ? 'flex' : 'none', flexWrap: 'wrap', gap: '5px' }}>
                                        <AntPOSCategories
                                            API={props.API}
                                            searchText={String(searchText)}
                                            searchConditions={categoriesSearchConditions}
                                            searchCategories={searchCategories1}
                                            multiSelect={false}
                                            selectedItems={selectedCats1}
                                            onLoadedData={onLoadedDataCat1}
                                            onSelectionChanged={onSelectionChangedCat1}
                                            visible={cats1Visible}
                                            allowQuery={allowQueryCat1}
                                        ></AntPOSCategories>
                                    </div>
                                )}
                            </div>
                            {categoriesRight && <div style={{ flex: '0 0 auto' }}>{categoriesRight}</div>}
                        </div>
                    )}
                </BoxItem>

                {isRender && props.cardview && !props.cardview?.dragable && (
                    <BoxItem ratio={1}>
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <AntCardViewList
                            ref={cardview}
                            API={props.API}
                            shouldRender={isQueryNow}
                            cardStyles={props.cardview.cardStyles ? props.cardview.cardStyles : props.cellStyles ? props.cellStyles : props.rowStyles}
                            cardClasses={
                                props.cardview.cardClasses ? props.cardview.cardClasses : props.cellCalsses ? props.cellCalsses : props.rowCalsses
                            }
                            cellActions={props.cellActions ? props.cellActions : gridRowActions}
                            {...props.cardview}
                            dataSource={cardViewDataSource.current.instance()}
                            rawDataSource={cardViewDataSource.current}
                            searchConditions={{
                                ...props.cardview?.searchConditions,
                                searchText: searchTextByBarcode ? searchTextByBarcode : undefined
                            }}
                            // onContentReady={(e) => {
                            //     console.log('ant-log-onContentReady-1 : ',);
                            //     if (e.element) {
                            //         console.log('ant-log-onContentReady-2 : ',);
                            //         e.element.addEventListener('resume', () => {
                            //             console.log('ant-log-resume : ',);
                            //             forceReload();
                            //         });
                            //         //   element.addEventListener('blur', handleBlur);
                            //     }
                            // }}
                            autoShowActions={props.cardview?.autoShowActions}
                            showActions={props.cardview?.showActions}
                            onItemClick={onItemClick}
                            // onItemMouseEnter={onItemMouseEnter}
                            onAction={onRowAction}
                        ></AntCardViewList>
                        {typeof props.renderFloatingAction === 'function' ? props.renderFloatingAction() : null}
                        </div>
                    </BoxItem>
                )}
                {isRender && props.cardview && props.cardview?.dragable && (
                    <BoxItem ratio={1}>
                        <AntPOSCardViewDragableList
                            ref={cardview}
                            API={props.API}
                            shouldRender={isQueryNow}
                            cardStyles={props.cardview.cardStyles ? props.cardview.cardStyles : props.cellStyles ? props.cellStyles : props.rowStyles}
                            cardClasses={
                                props.cardview.cardClasses ? props.cardview.cardClasses : props.cellCalsses ? props.cellCalsses : props.rowCalsses
                            }
                            cellActions={props.cellActions ? props.cellActions : gridRowActions}
                            {...props.cardview}
                            dataSource={cardViewDataSource.current.instance()}
                            rawDataSource={cardViewDataSource.current}
                            searchConditions={{
                                ...props.cardview?.searchConditions,
                                searchText: searchTextByBarcode ? searchTextByBarcode : undefined
                            }}
                            // onContentReady={(e) => {
                            //     console.log('ant-log-onContentReady-1 : ',);
                            //     if (e.element) {
                            //         console.log('ant-log-onContentReady-2 : ',);
                            //         e.element.addEventListener('resume', () => {
                            //             console.log('ant-log-resume : ',);
                            //             forceReload();
                            //         });
                            //         //   element.addEventListener('blur', handleBlur);
                            //     }
                            // }}
                            autoShowActions={props.cardview?.autoShowActions}
                            showActions={props.cardview?.showActions}
                            onItemClick={onItemClick}
                            // onItemMouseEnter={onItemMouseEnter}
                            onAction={onRowAction}
                        ></AntPOSCardViewDragableList>
                    </BoxItem>
                )}
            </Box>
        </React.Fragment>
    );
});
